import numeral from 'numeral';
import axios from "axios";
import { useToast } from "vue-toast-notification";
import { computed } from "vue";
import { useRoute } from "vue-router";
const RoundNumberType = {
    none: 'none',
    ceil: 'ceil',
    floor: 'floor',
    round: 'round',
};
numeral.register('locale', 'de', {
    delimiters: {
        thousands: ' ',
        decimal: ',',
    },
    abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't',
    },
    ordinal: function (number) {
        return '.';
    },
    currency: {
        symbol: '€',
    },
});
numeral.locale('de');
let uniqueIdCounter = 0;
export function uniqueId(prefix = '') {
    return prefix.toString() + (uniqueIdCounter++).toString();
}
export function wait(millis, { result, error } = {}) {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            if (error !== undefined) {
                reject(error);
            }
            else {
                resolve(result);
            }
        }, millis);
    });
}
export function optionalDestructingObject(o) {
    return o || {};
}
export function optionalDestructingArray(a) {
    return Array.isArray(a) ? a : [];
}
export function calculatePaginationRange(currentPage, totalPages, rangeSize = 5) {
    if (totalPages <= rangeSize) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, currentPage - Math.floor(rangeSize / 2));
    const endPage = Math.min(totalPages, startPage + rangeSize - 1);
    startPage = Math.max(1, endPage - rangeSize + 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
}
export function asArray(array) {
    return [].concat(array ?? []);
}
export function arrayToQueryStringParameter(array) {
    return asArray(array).sort().join(',');
}
export function convertDateFormat(dateString) {
    if (dateString === undefined || dateString === null) {
        return '';
    }
    const date = new Date(dateString); // Convert string to Date object
    if (isNaN(date.getTime())) {
        return dateString;
    }
    const formatter = new Intl.DateTimeFormat('en-UK', {
        month: 'short', // Abbreviated month name
        day: 'numeric', // Day of the month
        year: 'numeric', // Full year
    });
    return formatter.format(date);
}
export function currencyCodeToSymbol(code) {
    switch (code?.toString()?.toUpperCase()) {
        case 'USD':
            return '$';
        case 'EUR':
            return '€';
        default:
            return '';
    }
}
export function formatNumber(number, options = {}) {
    let { fractionDigits = 2, minFractionDigits = null, roundType = RoundNumberType.none, } = options;
    if (number === undefined || number === null) {
        return '-';
    }
    if (isNaN(number) || !isFinite(number)) {
        return '-';
    }
    if (minFractionDigits === true) {
        minFractionDigits = 2;
    }
    else if (minFractionDigits === false) {
        minFractionDigits = null;
    }
    if (minFractionDigits > 20) {
        console.warn(`utils.formatNumber(`, ...arguments, `)`, `shouldn't use minFractionDigits larger than 20 to prevent floating point errors.`);
    }
    let n;
    if (minFractionDigits !== null || roundType === RoundNumberType.none) {
        n = number;
    }
    else {
        const factor = Math.pow(10, fractionDigits);
        let roundFunction;
        switch (roundType) {
            case RoundNumberType.ceil:
                roundFunction = Math.ceil;
                break;
            case RoundNumberType.floor:
                roundFunction = Math.floor;
                break;
            case RoundNumberType.round:
            default:
                roundFunction = Math.round;
                break;
        }
        n = (roundFunction(number * factor) / factor);
    }
    if (minFractionDigits !== null) {
        return numeral(Number(n)).format(`0,0.${'0'.repeat(minFractionDigits)}[${'0'.repeat(10)}]`);
    }
    else {
        return numeral(Number(n)).format(`0,0.${'0'.repeat(fractionDigits)}`);
    }
}
export function getFlagImage(countryCode = '') {
    return `/assets/flags/${(countryCode || 'missing').toLowerCase()}.svg`;
}
export function pruneObject(o, propertiesToPrune = [null, undefined]) {
    return Object.fromEntries(Object.entries(o).filter(([, value]) => !propertiesToPrune.includes(value)));
}
const tagStyles = ' inline-block px-2 py-1 text-sm font-medium rounded-lg ';
export const statusStyles = {
    delivered: 'text-green-700 bg-green-50 ring-green-600/20' + tagStyles,
    dispatched: 'text-blue-900 bg-blue-50 ring-gray-100/10' + tagStyles,
    received: 'text-gray-900 bg-gray-100 ring-gray-100/10' + tagStyles,
    overdue: 'text-red-700 bg-red-50 ring-red-600/10' + tagStyles,
    pending: 'text-yellow-800 bg-yellow-50 ring-yellow-600/10' + tagStyles,
};
export function getOrderTagStatus(order) {
    const today = new Date();
    const deliveryDate = order.deliveryDate ? new Date(order.deliveryDate) : null;
    const dispatchDate = order.dispatchDate ? new Date(order.dispatchDate) : null;
    if (deliveryDate && deliveryDate < today) {
        return 'delivered';
    }
    if ((!deliveryDate || deliveryDate > today) && dispatchDate && dispatchDate < today) {
        return 'dispatched';
    }
    if ((!dispatchDate || dispatchDate > today) && (!deliveryDate || deliveryDate > today)) {
        return 'received';
    }
    return null;
}
;
export function getOrderStep(order) {
    const status = getOrderTagStatus(order);
    if (status === 'delivered')
        return 3;
    if (status === 'dispatched')
        return 2;
    if (status === 'received')
        return 1;
    return 0;
}
export function getRelevantDate(order) {
    const status = getOrderTagStatus(order);
    if (status === 'delivered')
        return getRelativeDate(order.deliveryDate);
    if (status === 'dispatched')
        return getRelativeDate(order.dispatchDate);
    if (status === 'received')
        return getRelativeDate(order.orderDate);
    return convertDateFormat(order.orderDate); // Default to order date if no other dates are set
}
;
export function getRelativeDate(dateString) {
    if (!dateString) {
        return '';
    }
    const today = new Date();
    const targetDate = new Date(dateString);
    // Calculate the difference in time
    const differenceInMilliseconds = today.getTime() - targetDate.getTime();
    const differenceInDays = Math.floor(differenceInMilliseconds / (1000 * 60 * 60 * 24));
    if (differenceInDays === 0) {
        return 'Today';
    }
    else if (differenceInDays === 1) {
        return 'Yesterday';
    }
    else if (differenceInDays <= 6 && differenceInDays > 0) {
        return `${differenceInDays} days ago`;
    }
    else {
        // If more than 4 weeks, return the formatted date
        return convertDateFormat(dateString); // Assuming `convertDateFormat` is your absolute date formatter
    }
}
;
export function getInvoiceTagStatus(invoice) {
    const today = new Date();
    const invoiceDate = new Date(invoice.dueDate);
    if (invoiceDate < today) {
        return 'overdue';
    }
    return 'pending';
}
;
export function camelCaseToSentenceCase(input) {
    // Step 1: Replace camelCase transitions with space
    const spaced = input.replace(/([a-z])([A-Z])/g, '$1 $2');
    // Step 2: Convert to lowercase
    const lowerCased = spaced.toLowerCase();
    // Step 3: Capitalize the first letter
    return lowerCased.charAt(0).toUpperCase() + lowerCased.slice(1);
}
export function ucfirst(str) {
    if (!str)
        return ''; // Handle empty or null strings
    return str.charAt(0).toUpperCase() + str.slice(1);
}
export function getOrder(order = '') {
    return order.startsWith('-') ? -1 : 1;
}
export function flipOrder(order = '') {
    const signSymbol = '-';
    return (order.startsWith(signSymbol)) ? order.substring(signSymbol.length) : signSymbol + order;
}
export async function downloadFile(fileUrl, filename, $t = null) {
    try {
        const response = await axios({
            url: fileUrl,
            method: 'GET',
            responseType: 'blob',
        });
        const blob = new Blob([response.data]);
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    catch (error) {
        if ($t !== null) {
            const $toast = useToast();
            $toast.error($t(`Could not download file ${filename}. Please try again.`));
        }
        else {
            throw error;
        }
    }
}
export function isNumeric(value) {
    return !isNaN(value) && !isNaN(parseFloat(value));
}
export function getDefaultPagination(data) {
    return {
        ...{
            updated: '',
            startIndex: 1,
            itemsPerPage: 10,
            totalItems: 0,
            currentItemCount: 0,
            nextLink: '',
            previousLink: '',
            pagingLinkTemplate: '',
        },
        ...data
    };
}
export function isNullOrUndefined(value) {
    return value === undefined || value === null;
}
export function getIfMatchForETags(...etags) {
    return etags
        .filter((v) => !isNullOrUndefined(v))
        .map((etag) => `"${etag}"`).join(',');
}
// Short lengths of all products (not limited to an article group)
export function useShortLengthsOverview() {
    const route = useRoute();
    return computed(() => route.query.remainders === '1' && !route.query.articleGroup && !route.query.articleGrouping);
}
